"use strict";
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
// Contains types for the questionnaire sections configuration
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAndValidateSurveySections = void 0;
/**
 * Get the complete SurveySections object from the configuration. This sets the
 * default values of the configuration when not specified so that all values are
 * specific.
 *
 * @param {SurveySectionsConfig} sections The survey sections configuration
 * @returns {SurveySections} The complete SurveySections object
 */
const getAndValidateSurveySections = (sections) => {
    // Map the sections that are in a repeated block to the section that define this block
    const sectionsInRepeatedBlock = {};
    Object.keys(sections).forEach((sectionName) => {
        const sectionConfig = sections[sectionName];
        if (sectionConfig.repeatedBlock) {
            const repeatedSections = sectionConfig.repeatedBlock.sections;
            repeatedSections.forEach((repeatedSectionName) => {
                sectionsInRepeatedBlock[repeatedSectionName] = sectionName;
            });
        }
    });
    const sectionsWithDefaults = {};
    // Make sure the navMenu is defined for each section
    Object.keys(sections).forEach((sectionName) => {
        var _a, _b, _c, _d, _e;
        const sectionConfig = sections[sectionName];
        if (sectionConfig.repeatedBlock !== undefined) {
            if (sectionConfig.nextSection === null) {
                throw new Error(`Section "${sectionName}" cannot have a nextSection set to null when it is a repeated block.`);
            }
            sectionsWithDefaults[sectionName] = Object.assign(Object.assign({ sectionName }, sectionConfig), { type: 'repeatedBlock', nextSection: sectionConfig.nextSection, navMenu: (_a = sectionConfig.navMenu) !== null && _a !== void 0 ? _a : { type: 'inNav', menuName: (_b = sectionConfig.title) !== null && _b !== void 0 ? _b : '' }, repeatedBlock: sectionConfig.repeatedBlock });
        }
        else if (sectionsInRepeatedBlock[sectionName]) {
            sectionsWithDefaults[sectionName] = Object.assign(Object.assign({ sectionName }, sectionConfig), { type: 'section', navMenu: (_c = sectionConfig.navMenu) !== null && _c !== void 0 ? _c : {
                    type: 'hidden',
                    parentSection: sectionsInRepeatedBlock[sectionName]
                }, repeatedBlockSection: sectionsInRepeatedBlock[sectionName] });
        }
        else {
            sectionsWithDefaults[sectionName] = Object.assign(Object.assign({ sectionName }, sectionConfig), { type: 'section', navMenu: (_d = sectionConfig.navMenu) !== null && _d !== void 0 ? _d : { type: 'inNav', menuName: (_e = sectionConfig.title) !== null && _e !== void 0 ? _e : '' } });
        }
    });
    // Validate each section to make sure every configuration is correct
    Object.entries(sectionsWithDefaults).forEach(([sectionName, sectionConfig]) => {
        // Give warnings if there are undefined sections in the configuration
        if (sectionConfig.previousSection && !sectionsWithDefaults[sectionConfig.previousSection]) {
            throw new Error(`Section "${sectionConfig.previousSection}" is referenced in "previousSection" of section "${sectionName}", but it is not defined in the sections config.`);
        }
        if (sectionConfig.nextSection && !sectionsWithDefaults[sectionConfig.nextSection]) {
            throw new Error(`Section "${sectionConfig.nextSection}" is referenced in "nextSection" of section "${sectionName}", but it is not defined in the sections config.`);
        }
        if (sectionConfig.type === 'repeatedBlock') {
            // All repeated sections should exist
            const repeatedSections = sectionConfig.repeatedBlock.sections;
            repeatedSections.forEach((repeatedSectionName) => {
                if (!sectionsWithDefaults[repeatedSectionName]) {
                    throw new Error(`Section "${repeatedSectionName}" is referenced in "repeatedBlock" of section "${sectionName}", but it is not defined in the sections config.`);
                }
            });
            // The selectionSection should exist if specified
            if (sectionConfig.repeatedBlock.selectionSectionId) {
                if (!sectionsWithDefaults[sectionConfig.repeatedBlock.selectionSectionId]) {
                    throw new Error(`Section "${sectionConfig.repeatedBlock.selectionSectionId}" is referenced in "selectionSectionId" of section "${sectionName}", but it is not defined in the sections config.`);
                }
            }
        }
        // Validate that the section in the navMenu exists
        if (sectionConfig.navMenu.type === 'hidden') {
            const parentSection = sectionConfig.navMenu.parentSection;
            if (!sectionsWithDefaults[parentSection]) {
                throw new Error(`Parent section "${parentSection}" is referenced in "navMenu" of section "${sectionName}", but it is not defined in the sections config.`);
            }
            else if (sectionsWithDefaults[parentSection].navMenu.type !== 'inNav') {
                throw new Error(`Parent section "${parentSection}" referenced in "navMenu" of section "${sectionName}" is not visible in the navigation menu.`);
            }
        }
    });
    // Make sure there is a first and last section
    const firstSection = Object.keys(sectionsWithDefaults).find((sectionName) => sectionsWithDefaults[sectionName].previousSection === null);
    if (!firstSection) {
        throw new Error('No first section defined. Make sure at least one section has "previousSection" set to null.');
    }
    const lastSection = Object.keys(sectionsWithDefaults).find((sectionName) => sectionsWithDefaults[sectionName].nextSection === null);
    if (!lastSection) {
        throw new Error('No last section defined. Make sure at least one section has "nextSection" set to null.');
    }
    // FIXME Make further check? Do we have cycles? Are all sections used? are there many first or last sections?
    return sectionsWithDefaults;
};
exports.getAndValidateSurveySections = getAndValidateSurveySections;
//# sourceMappingURL=SectionConfig.js.map